import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import toast from "components/partials/toast/toast";

import { customInstance } from "config/mutator";
import { getCampaign } from "generated/api/campaign";
import type { CampaignTemplateWithDataDTO } from "generated/schemas";

import { useCurrentClient } from "hooks/api/use-clients";

import { useCampaignWithWriterContext } from "components/modals/create-campaign-with-writer-modal/context/campaign-with-writer-context";
import {
  buildCampaignFromTemplateRequest,
  getDeliverablesFromTemplateData,
  getTemplateWriterValidationError,
  hasSelectedEmailDeliverables,
} from "components/modals/create-campaign-with-writer-modal/create-campaign-with-writer-modal.helper";
import type { CreateCampaignWithWriterFormData } from "components/modals/create-campaign-with-writer-modal/create-campaign-with-writer-modal.types";

import { getCampaignPaths } from "utilities/app-routes";
import { getErrorMessage } from "utilities";
import {
  markEmailWriterPollingStarted,
} from "utilities/email-writer-polling-session";

export const useCreateCampaignWithWriter = () => {
  const { setIsOpen } = useCampaignWithWriterContext();
  const currentClient = useCurrentClient();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createCampaign = useCallback(
    async (formData: CreateCampaignWithWriterFormData) => {
      if (!formData.campaignTemplateId || !formData.programStrategy) {
        toast.error({
          data: {
            title: "Failed to create campaign",
            message: "Campaign setup details are missing. Go back to step 1 and try again.",
          },
        });
        return;
      }

      if (!formData.campaignName?.trim()) {
        toast.error({
          data: {
            title: "Failed to create campaign",
            message: "Campaign name is required.",
          },
        });
        return;
      }

      setIsSubmitting(true);

      try {
        const clientId = currentClient.id;
        const campaignApi = getCampaign();
        const template = await customInstance<CampaignTemplateWithDataDTO>({
          url: `/api/campaign-templates/${formData.campaignTemplateId}`,
          method: "GET",
        });

        const templateValidationError = getTemplateWriterValidationError(template.data);
        if (templateValidationError) {
          toast.error({
            data: {
              title: "Failed to create campaign",
              message: templateValidationError,
            },
          });
          return;
        }

        const request = buildCampaignFromTemplateRequest(formData, template.data);
        if (!request) {
          toast.error({
            data: {
              title: "Failed to create campaign",
              message: "Campaign template data is incomplete.",
            },
          });
          return;
        }

        const createdCampaign = await campaignApi.createCampaignFromTemplate({ clientId }, request);

        if (createdCampaign?.id) {
          toast.success({
            data: {
              title: "Created campaign",
            },
          });

          const deliverables = getDeliverablesFromTemplateData(template.data);
          if (
            formData.includeCreatives &&
            hasSelectedEmailDeliverables(deliverables, formData.deliverables ?? [])
          ) {
            markEmailWriterPollingStarted(createdCampaign.id, clientId);
            toast.info({
              data: {
                title: "Email writing in progress",
                message:
                  "Written emails content will appear in creative details, and notification will be sent upon completion.",
              },
              autoClose: false,
            });
          }

          const { campaignStrategyPath } = getCampaignPaths({
            clientId,
            campaignId: createdCampaign.id,
          });
          navigate(campaignStrategyPath);
        }

        setIsOpen(false);
      } catch (error) {
        toast.error({
          data: {
            title: "Failed to create campaign",
            message: getErrorMessage(error) || "Please try again.",
          },
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [currentClient, navigate, setIsOpen],
  );

  return { createCampaign, isSubmitting };
};
